import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";

/**
 * Magic Links management data access (W2). Reads/mutations go through Edge
 * Functions via functions.invoke — no client-side Supabase writes. The flat
 * tab uses list_my_magic_links with explicit scope, while
 * list_magic_links_for_signal remains for single-signal/detail contexts.
 */

export type MagicLinkStatus = "active" | "revoked" | "expired";

export interface MagicLinkRow {
  id: string;
  signalId: string;
  company: string;
  headline: string;
  recipientName: string;
  recipientEmail: string;
  status: MagicLinkStatus;
  createdAt: string;
  expiresAt: string | null;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  snoozeCount: number;
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(`${name}: ${error.message}`);
  return data as T;
}

interface RawLink {
  magic_link_id: string;
  signal_id: string;
  recipient_display_name: string | null;
  recipient_email: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  first_opened_at: string | null;
  last_opened_at: string | null;
  open_count: number | null;
  snooze_count: number | null;
  signal?: {
    feed_title?: string | null;
    related_organization_name?: string | null;
  } | null;
}

function statusOf(link: RawLink): MagicLinkStatus {
  if (link.revoked_at) return "revoked";
  if (link.expires_at && new Date(link.expires_at).getTime() < Date.now()) {
    return "expired";
  }
  return "active";
}

export type MagicLinksScope = "mine" | "all";

export async function fetchMyMagicLinks(scope: MagicLinksScope): Promise<MagicLinkRow[]> {
  const res = await invoke<{ data: { magic_links: RawLink[] } }>("list_my_magic_links", {
    scope,
    limit: 200,
  });
  return (res.data?.magic_links ?? []).map((l) => ({
    id: l.magic_link_id,
    signalId: l.signal_id,
    company: l.signal?.related_organization_name ?? "Unknown account",
    headline: l.signal?.feed_title ?? "",
    recipientName: l.recipient_display_name || l.recipient_email || "Recipient",
    recipientEmail: l.recipient_email ?? "",
    status: statusOf(l),
    createdAt: l.created_at,
    expiresAt: l.expires_at,
    openCount: l.open_count ?? 0,
    firstOpenedAt: l.first_opened_at,
    lastOpenedAt: l.last_opened_at,
    snoozeCount: l.snooze_count ?? 0,
  }));
}

export async function revokeMagicLink(magicLinkId: string): Promise<void> {
  await invoke("revoke_magic_link", { magic_link_id: magicLinkId });
}

/**
 * Create a Magic Link for a signal (admin/super_admin only - the
 * create_magic_link Edge Function enforces this server side). Same Edge
 * Function and token URL construction the modal's Share action used before
 * Settings became the sole creation surface.
 */
export async function createMagicLink(
  signalId: string,
  recipientId: string
): Promise<{ url: string }> {
  const res = await invoke<{ data: { token: string } }>("create_magic_link", {
    signal_id: signalId,
    recipient_user_id: recipientId,
  });
  const token = res.data?.token;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return { url: token ? `${origin}/s/${token}` : "" };
}
